import { FC } from "react";
import classNames from "classnames";

import { useAvoidFloatOrphan } from "../../../hooks/useAvoidFloatOrphan";

import { TextWithImageBlockProps } from "./TextWithImageBlock.type";

const imgProps = (imageAlt?: string) =>
	imageAlt ? { alt: imageAlt, title: imageAlt } : { role: "presentation" as const };

const hasContent = (value?: string) => typeof value === "string" && value.trim().length > 0;

// Subtitles are authored with a leading dash for the text column ("- APPLiA President").
// That reads as a typo once the line sits on its own inside the plate.
const stripLeadingDash = (value: string) => value.replace(/^\s*[-–—]\s*/, "");

const TextWithImageBlock: FC<TextWithImageBlockProps> = ({ data, id, marginalia }) => {
	const { text, align = "justify", imageUrl, imageSize = "normal", type, imageAlt, title, subtitle, roundedImage = true, imageAccent } = data;
	const hasNameplate = imageAccent === "nameplate" && hasContent(title);
	// Only the floating variants let text run under the image, so only they can strand a line.
	const { floatRef, textRef } = useAvoidFloatOrphan<HTMLDivElement, HTMLDivElement>();
	const wrapsAroundImage = type === "image-left" || type === "image-right";

	if (type === "image-left-column" || type === "image-right-column") {
		return (
			<div
				id={id}
				className={classNames("flex flex-col items-start gap-8", {
					"sm:flex-row": type === "image-left-column",
					"sm:flex-row-reverse": type === "image-right-column",
				})}
			>
				<div
					className={classNames("shrink-0 w-full", {
						"sm:w-1/2": imageSize === "normal",
						"sm:w-1/3": imageSize === "small",
						"sm:w-2/3": imageSize === "big",
					})}
				>
					<div className="relative">
						{imageAccent === "offset" && (
							<div aria-hidden="true" className="absolute -bottom-3 -left-3 w-full h-full rounded-2xl bg-primary" />
						)}
						<img
							{...imgProps(imageAlt)}
							className={classNames("relative", { "rounded-2xl": roundedImage, "w-full": Boolean(imageAccent) })}
							src={imageUrl}
						/>
						{hasNameplate && (
							<div className="relative -mt-12 mx-4 rounded-xl bg-secondary px-4 py-3 shadow-lg">
								<span className="block font-semibold leading-tight text-white" dangerouslySetInnerHTML={{ __html: title as string }} />
								{hasContent(subtitle) && (
									<span
										className="block mt-0.5 text-sm leading-tight text-white/80"
										dangerouslySetInnerHTML={{ __html: stripLeadingDash(subtitle as string) }}
									/>
								)}
							</div>
						)}
					</div>
				</div>
				{marginalia && <div className="w-full">{marginalia}</div>}
				<div className="flex-1 min-w-0">
					{/* The nameplate already carries these. */}
					{!hasNameplate && hasContent(title) && (
						<h3 className={classNames("leading-relaxed", subtitle ? "mb-0" : "mb-4")} dangerouslySetInnerHTML={{ __html: title as string }} />
					)}
					{!hasNameplate && hasContent(subtitle) && (
						<h4 className="leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: subtitle as string }} />
					)}
					{Array.isArray(text) ? (
						text.map((paragraph, index) => (
							<p
								key={`${id}-paragraph-${index}`}
								className={classNames("leading-relaxed", { "mb-6": index + 1 !== text.length })}
								style={{ textAlign: align }}
								dangerouslySetInnerHTML={{ __html: paragraph }}
							/>
						))
					) : (
						<p className="leading-relaxed" style={{ textAlign: align }} dangerouslySetInnerHTML={{ __html: text }} />
					)}
				</div>
			</div>
		);
	}

	return (

		<div id={id}
			className={classNames("clear-both overflow-auto", {
				"md:flex items-center": type === "image-special",
			})}>

			<div
				ref={wrapsAroundImage ? floatRef : undefined}
				className={classNames("w-full mb-4 sm:mb-0 sm:pb-4", {
					"sm:float-left sm:pr-8 xl:pr-12": type === "image-left" || type === "image-special",
					"float-right": type === "image-special",
					// Type: "image-right" hide this image and show image under all paragraphs on mobile
					"hidden sm:block sm:float-right sm:pl-8 xl:pl-12": type === "image-right",
					"sm:w-1/2": imageSize === "normal",
					"sm:w-1/3": imageSize === "small",
					"sm:w-2/3": imageSize === "big",
				})}
			>
			<img
				{...(imageAlt && {
					alt: imageAlt,
					title: imageAlt,
				})}
				{...(!imageAlt && {
					role: "presentation",
				})}
				className={classNames({ "rounded-2xl": roundedImage })}
				src={imageUrl}
			/>
			</div>

			{/* A plain block, so the lines wrap around the float exactly as before. On the widths
			    where that wrap would strand a line or two under the image, `useAvoidFloatOrphan`
			    switches this to a block formatting context and the text stays beside the image. */}
			<div ref={wrapsAroundImage ? textRef : undefined}>
				{marginalia && <div className="mb-4">{marginalia}</div>}

				{/* Optional: text title */}
				{typeof title === "string" && title.trim().length > 0 && (
					<h3 className={classNames("leading-relaxed", subtitle ? "mb-1" : "mb-4")} dangerouslySetInnerHTML={{ __html: title }} />
				)}
				{typeof subtitle === "string" && subtitle.trim().length > 0 && (
					<h4 className="leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: subtitle }} />
				)}

				{Array.isArray(text) ? (
					// Text type = array: render multiple paragraphs
					text.map((paragraph, index) => (
						<p
							key={`${id}-paragraph-${index}`}
							className={classNames("leading-relaxed", { "mb-6": index + 1 !== text.length })}
							style={{ textAlign: align }}
							dangerouslySetInnerHTML={{ __html: paragraph }}
						/>
					))
				) : (
					// Text type = string: render single paragraph
					<p className="leading-relaxed" style={{ textAlign: align }} dangerouslySetInnerHTML={{ __html: text }} />
				)}
			</div>

			{/* Type: "image-right" put image under all paragraphs on mobile */}
			{type === "image-right" && (
				<div className="block sm:hidden">
					<img
						{...(imageAlt && {
							alt: imageAlt,
							title: imageAlt,
						})}
						{...(!imageAlt && {
							role: "presentation",
						})}
						className={classNames("w-full mt-4", { "rounded-2xl": roundedImage })}
						src={imageUrl}
					/>
				</div>
			)}

		</div>
	);
};

export { TextWithImageBlock };
